import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarCheck, Clock } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export default function VisitCalendar({ leads }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(today);

  const visits = useMemo(() => {
    const out = [];
    for (const l of leads || []) {
      const sv = l.site_visit || {};
      if (sv.status === "confirmed" && sv.confirmed) {
        out.push({ kind: "confirmed", slot: sv.confirmed.slot, lead: l });
      } else {
        (sv.proposed || []).forEach((p) => out.push({ kind: "proposed", slot: p.slot, lead: l }));
      }
    }
    return out.filter((v) => v.slot && !isNaN(new Date(v.slot)));
  }, [leads]);

  const byDay = useMemo(() => {
    const m = {};
    for (const v of visits) {
      const k = dayKey(new Date(v.slot));
      (m[k] = m[k] || []).push(v);
    }
    return m;
  }, [visits]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))];

  const fmtTime = (slot) => new Date(slot).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  const selectedVisits = selected ? byDay[dayKey(selected)] || [] : [];

  return (
    <div data-testid="visit-calendar">
      <div className="dash-card">
        <div className="flex items-center justify-between border-b border-black/10 px-5 py-4">
          <p className="font-serif text-2xl text-black" data-testid="calendar-month-label">{MONTHS[month]} {year}</p>
          <div className="flex items-center gap-2">
            <span className="mr-3 hidden font-mono text-[10px] uppercase tracking-[0.2em] text-black/45 sm:block">
              {visits.filter((v) => v.kind === "confirmed").length} confirmed · {visits.filter((v) => v.kind === "proposed").length} proposed
            </span>
            <button className="dash-btn-ghost" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev" aria-label="Previous month"><ChevronLeft size={14} /></button>
            <button className="dash-btn-ghost" onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(today); }} data-testid="calendar-today">Today</button>
            <button className="dash-btn-ghost" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next" aria-label="Next month"><ChevronRight size={14} /></button>
          </div>
        </div>
        <div className="grid grid-cols-7">
          {WDAYS.map((w) => (
            <div key={w} className="border-b border-black/10 px-2 py-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-black/45">{w}</div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} className="min-h-[84px] border-b border-r border-black/5 bg-black/[0.015]" />;
            const dayVisits = byDay[dayKey(d)] || [];
            const isToday = dayKey(d) === dayKey(today);
            const isSelected = selected && dayKey(d) === dayKey(selected);
            return (
              <button
                key={dayKey(d)}
                onClick={() => setSelected(d)}
                data-testid={`calendar-day-${d.getDate()}`}
                className={`min-h-[84px] border-b border-r border-black/5 p-1.5 text-left align-top transition-colors hover:bg-black/[0.04] ${isSelected ? "bg-black/[0.06] outline outline-1 outline-black" : ""}`}
              >
                <span className={`inline-flex h-6 w-6 items-center justify-center font-mono text-[11px] ${isToday ? "bg-black text-white" : "text-black/60"}`}>
                  {d.getDate()}
                </span>
                <span className="mt-1 block space-y-1">
                  {dayVisits.slice(0, 2).map((v, j) => (
                    <span
                      key={j}
                      className={`block truncate px-1.5 py-0.5 text-[10px] ${v.kind === "confirmed" ? "bg-black text-white" : "border border-black/25 text-black/60"}`}
                    >
                      {fmtTime(v.slot)} · {v.lead.name.split(" ")[0]}
                    </span>
                  ))}
                  {dayVisits.length > 2 && <span className="block px-1.5 font-mono text-[9px] text-black/40">+{dayVisits.length - 2} more</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="dash-card mt-4 p-5" data-testid="calendar-day-detail">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">
          {selected ? selected.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) : "Pick a day"}
        </p>
        {selectedVisits.length === 0 ? (
          <p className="mt-3 text-sm text-black/45">No visits on this day.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {selectedVisits.map((v, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3 border border-black/10 px-4 py-3" data-testid={`visit-item-${i}`}>
                {v.kind === "confirmed" ? <CalendarCheck size={15} className="text-black" /> : <Clock size={15} className="text-black/40" />}
                <span className="font-medium text-black">{fmtTime(v.slot)}</span>
                <span className="text-sm text-black/70">{v.lead.name} — {v.lead.property_title}</span>
                <span className="text-[11px] text-black/45">host: {v.lead.assigned_to_name || "unassigned"}</span>
                <span className={`ml-auto px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${v.kind === "confirmed" ? "bg-black text-white" : "bg-neutral-200 text-neutral-600"}`}>{v.kind}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
